import {useQuery} from "react-query";
import {requestWithToken} from "@/utils/functions";
import {SERVICE_API_URL} from "@/utils/constants";
import {
    GetInstanceMonitoringPayload,
    GetInstancePayload,
    GetInstanceLogsPayload,
    GetListInstancesPayload,
    INSTANCE_ACTIONS,
    INSTANCE_STATES,
    SubmitInstanceActionPayload,
    SubmitInstanceCreatePayload,
    SubmitInstanceUpdatePayload
} from "../types/api-payload.ts";
import {
    GetInstanceMonitoringResponse, GetInstanceRemoteTicketResponse,
    GetInstanceResponse,
    GetInstanceSummaryResponse,
    GetListInstancesResponse,
    GetInstanceHealthCheckResponse,
    GetInstanceLogsResponse
} from "../types/api-response.ts";
import reactQueryKey from "../utils/react-query-key.ts";
import qs from "qs";

export function resolveInstanceList({
                                        service_uuid
                                    }: GetListInstancesPayload) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<GetListInstancesResponse>(
        reactQueryKey.resolveInstanceListKey({service_uuid}),
        async () => {
            const response = await requestWithToken(
                `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/instances`,
                "GET"
            );

            return response?.data;
        }, {
            retry: false
        }
    );
}

export async function resolveInstanceSummaryAsync({
                                                      service_uuid,
                                                      instance_id
                                                  }: GetInstancePayload) {
    const response = await requestWithToken(
        `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/instances/${instance_id}/summary`,
        "GET"
    );

    return response?.data;
}

export function resolveInstanceSummary({
                                           service_uuid,
                                           instance_id
                                       }: GetInstancePayload) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<GetInstanceSummaryResponse>(
        reactQueryKey.resolveInstanceSummaryKey({service_uuid, instance_id}),
        async () => await resolveInstanceSummaryAsync({service_uuid, instance_id}),
        {
            retry: false,
            cacheTime: 5,
            refetchInterval: 15000,
            keepPreviousData: true
        }
    );
}

export function resolveInstanceDetail({
                                          service_uuid,
                                          instance_id
                                      }: GetInstancePayload, isWaitingUpdate: boolean | INSTANCE_ACTIONS | INSTANCE_STATES) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<GetInstanceResponse>(
        reactQueryKey.resolveInstanceDetailKey({service_uuid, instance_id}),
        async () => {
            const response = await requestWithToken(
                `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/instances/${instance_id}`,
                "GET"
            );

            return response?.data;
        }, {
            retry: false,
            refetchInterval: isWaitingUpdate ? 3000 : false
        }
    );
}

export function resolveInstanceMonitoring({
                                              service_uuid,
                                              instance_id,
                                              query,
                                          }: GetInstanceMonitoringPayload) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<GetInstanceMonitoringResponse>(
        reactQueryKey.resolveInstanceMonitoringKey({service_uuid, instance_id, query}),
        async () => {
            const queryString = qs.stringify(query)

            const response = await requestWithToken(
                `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/instances/${instance_id}/monitoring?${queryString}`,
                "GET"
            );

            return response?.data;
        }, {
            retry: false,
            refetchInterval: 15000
        }
    );
}

export function resolveInstanceRemoteTicket({
                                                service_uuid,
                                                instance_id
                                            }: GetInstancePayload) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<GetInstanceRemoteTicketResponse>(
        reactQueryKey.resolveInstanceConsoleRemoteTicketKey({service_uuid, instance_id}),
        async () => {
            const response = await requestWithToken(
                `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/instances/${instance_id}/vnc`,
                "GET"
            );

            return response?.data;
        }, {
            retry: false,
            suspense: true
        }
    );
}

export async function mutateInstanceCreate({service_uuid, payload}: SubmitInstanceCreatePayload) {
    const response = await requestWithToken(
        `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/instances`,
        "POST",
        payload
    )

    return response?.data
}

export async function mutateInstanceAction({service_uuid, instance_id, perform}: SubmitInstanceActionPayload) {
    const response = await requestWithToken(
        `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/instances/${instance_id}/perform`,
        "POST",
        {perform}
    )

    return response?.data
}

export async function mutateInstanceUpdate({service_uuid, instance_id, payload}: SubmitInstanceUpdatePayload) {
    const response = await requestWithToken(
        `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/instances/${instance_id}`,
        "PATCH",
        payload
    )

    return response?.data
}

export function resolveInstanceHealthCheck({
                                              service_uuid,
                                              instance_id,
                                              enabled = true
                                          }: GetInstancePayload & { enabled?: boolean }) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<GetInstanceHealthCheckResponse>(
        reactQueryKey.resolveInstanceHealthCheckKey({service_uuid, instance_id}),
        async () => {
            try {
                const response = await requestWithToken(
                    `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/marketplaces/${instance_id}/healthz`,
                    "GET"
                );
                return response?.data;
            } catch (exception) {
                console.log(exception);
                // Return error state for health check
                throw exception;
            }
        }, {
            enabled: enabled && Boolean(service_uuid && instance_id),
            retry: false,
            refetchOnWindowFocus: false,
            refetchInterval: enabled ? 30000 : false, // Poll every 30 seconds if enabled
            staleTime: 10000, // Consider data stale after 10 seconds
            cacheTime: 30000  // Keep in cache for 30 seconds
        }
    );
}

export function resolveInstanceLogs({
                                       service_uuid,
                                       instance_id,
                                       container = 'n8n',
                                       lines = 100,
                                       enabled = true
                                   }: GetInstanceLogsPayload & { enabled?: boolean }) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<GetInstanceLogsResponse>(
        reactQueryKey.resolveInstanceLogsKey({service_uuid, instance_id, container, lines}),
        async () => {
            try {
                const params = new URLSearchParams();
                if (container) params.append('container', container);
                if (lines) params.append('lines', lines.toString());
                
                const response = await requestWithToken(
                    `${SERVICE_API_URL}/v2/services/${service_uuid}/s10003/marketplaces/${instance_id}/logs?${params.toString()}`,
                    "GET"
                );
                return response?.data;
            } catch (exception) {
                console.log(exception);
                throw exception;
            }
        }, {
            enabled: enabled && Boolean(service_uuid && instance_id),
            retry: false,
            refetchOnWindowFocus: false,
            staleTime: 5000, // Consider data stale after 5 seconds for logs
            cacheTime: 10000  // Keep in cache for 10 seconds
        }
    );
}